/**
 * @file   src\Pages\RevenueSummaryReport.tsx
 * @brief  Car wash owner revenue summary grouped by channel, wash type, and bay.
 * @date   SEP, 2026
 * @author ZCO Engineer
 * @copyright (c) 2026, ZCO
 */

import { Container, Breadcrumb, useNavigate, useEffect, useOutletContext, useState, Button, React } from '../utils/thirdpartyComponents';
import { AppContextType } from '../interfaces/generalInterface';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';
import { MONTHS_OPTIONS } from '../utils/constants';
import { EquipmentType } from '../utils/enums';
import { pageURLs } from '../utils/pageUrls';
import axios from '../config/axiosConfig';
import { LocationApi, RevenueApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';
import CWSelect from '../components/CWSelect';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/subscriptions.scss';
import './RevenueSummaryReport.scss';

interface BayAmount {
  name: string;
  amount: number;
}

interface WashSection {
  name: string;
  bays: BayAmount[];
  subtotal: number;
}

interface ChannelSummary {
  title: string;
  total: number;
  sections: WashSection[];
}

interface RevenueBayRow {
  BayTypeID: number;
  BayName: string;
  Total: number;
}

const BAY_TYPE_LABELS: Record<number, string> = {
  [EquipmentType.Tunnel]: 'Tunnel',
  [EquipmentType['In Bay']]: 'InBay',
  [EquipmentType['Self Serve']]: 'Self Serve',
  [EquipmentType['Dog Wash']]: 'Dog Wash',
  [EquipmentType.Vacuum]: 'Vacuum',
};

const SECTION_ORDER = [
  EquipmentType['In Bay'],
  EquipmentType.Tunnel,
  EquipmentType.Vacuum,
  EquipmentType['Self Serve'],
  EquipmentType['Dog Wash'],
];

const formatAmount = (amount: number) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return '$0';
  }
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
};

const buildSections = (rows: RevenueBayRow[] = []): WashSection[] => {
  const grouped = new Map<number, BayAmount[]>();

  rows.forEach((row) => {
    const typeId = Number(row.BayTypeID);
    const bays = grouped.get(typeId) ?? [];
    bays.push({
      name: row.BayName || 'Bay',
      amount: Number(row.Total) || 0,
    });
    grouped.set(typeId, bays);
  });

  const orderedIds = [
    ...SECTION_ORDER.filter((typeId) => grouped.has(typeId)),
    ...Array.from(grouped.keys()).filter((typeId) => !SECTION_ORDER.includes(typeId)),
  ];

  return orderedIds.map((typeId) => {
    const bays = grouped.get(typeId) ?? [];
    return {
      name: BAY_TYPE_LABELS[typeId] + " Single Washes Revenue" || 'Other',
      bays,
      subtotal: bays.reduce((sum, bay) => sum + bay.amount, 0),
    };
  });
};

const RevenueSummaryReport = () => {
  const { updateData } = useOutletContext<AppContextType>();
  const navigate = useNavigate();
  const toaster = new MessageToaster();
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [location, setLocation] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [locations, setLocations] = useState<Option[]>([]);
  const [summary, setSummary] = useState<ChannelSummary[]>([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [reportVisible, setReportVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ year?: string; month?: string; location?: string }>({});

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - 2024 + 1 },
    (_, index) => {
      const value = (2024 + index).toString();
      return { label: value, value };
    }
  );

  useEffect(() => {
    updateData({
      heading: 'Revenue Summary Report',
      url: '',
      showBackButton: false,
      showMainMenu: true,
      activeLink: 'reports',
    });
    getCompanyLocations();
  }, []);

  const getCompanyLocations = async () => {
    try {
      const { data } = await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
      if (data?.error === 0 && Array.isArray(data?.data)) {
        const locationList: LocationItem[] = data.data;
        setLocations(locationList.map((item) => ({
          label: item.locationName,
          value: item.locationId,
        })));
      } else {
        toaster.toastError(data?.message || 'Failed to load locations.');
      }
    } catch (error: any) {
      toaster.toastError(error?.response?.data?.message || error?.message || 'Failed to load locations.');
    }
  };

  const hasDateRange = Boolean(fromDate || toDate);

  const clearMonth = () => {
    setMonth('');
    setErrors((current) => ({ ...current, month: undefined }));
  };

  const generateReport = async () => {
    const nextErrors: { year?: string; month?: string; location?: string } = {};
    if (!year) {
      nextErrors.year = 'Please select a year.';
    }
    if (!month && !hasDateRange) {
      nextErrors.month = 'Please select a month or a date range.';
    }
    if (!location) {
      nextErrors.location = 'Please select a location.';
    }
    setErrors(nextErrors);
    if (nextErrors.year || nextErrors.month || nextErrors.location) {
      // toaster.toastError('Please select location, month, and year before generating the report.');
      return;
    }

    const queryParams = new URLSearchParams();
    queryParams.append('locationId', String(location));
    if (year) {
      queryParams.append('year', year);
    }
    if (month) {
      queryParams.append('month', String(parseInt(month, 10)));
    }
    if (fromDate) {
      queryParams.append('fromDate', moment(fromDate).format('YYYY-MM-DD'));
    }
    if (toDate) {
      queryParams.append('toDate', moment(toDate).format('YYYY-MM-DD'));
    }

    try {
      setLoading(true);
      const { data } = await axios.get(`${RevenueApi.CARWASH_OWNER_REVENUE_SUMMARY_REPORT}?${queryParams.toString()}`);
      if (data?.error !== 0) {
        toaster.toastError(data?.message || 'Failed to load the revenue summary report.');
        return;
      }

      const pwaTotal = Number(data?.data?.pwaTotal) || 0;
      const paxTotal = Number(data?.data?.paxTotal) || 0;
      setSummary([
        {
          title: 'PWA Revenue Summary',
          total: pwaTotal,
          sections: buildSections(data?.data?.pwaData),
        },
        {
          title: 'Pax Revenue Summary',
          total: paxTotal,
          sections: buildSections(data?.data?.paxData),
        },
      ]);
      setGrandTotal(pwaTotal + paxTotal);
      setReportVisible(true);
    } catch (error: any) {
      toaster.toastError(error?.response?.data?.message || error?.message || 'Failed to load the revenue summary report.');
    } finally {
      setLoading(false);
    }
  };

  const clearDates = () => {
    setFromDate(null);
    setToDate(null);
  };

  const selectedYear = yearOptions.find((option) => option.value === year);
  const selectedMonth = MONTHS_OPTIONS.find((option) => option.value === month);
  const selectedLocation = locations.find((option) => String(option.value) === String(location));

  return (
    <div className="manage-carwash-container order-list-container multi-location-wrap revenue-summary-report">
      <Container>
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => navigate(`/${pageURLs.dashboard}`)}>Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item active>Revenue Summary Report</Breadcrumb.Item>
        </Breadcrumb>
        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
              <CWSelect
                id="summary-year"
                name="summary-year"
                options={yearOptions}
                label="Select Year"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                errorMessage={errors.year}
                values={selectedYear ? [selectedYear] : []}
                onChange={(option: Option[]) => {
                  setYear(option[0]?.value?.toString() || '');
                  setErrors((current) => ({ ...current, year: undefined }));
                }}
              />
              <CWSelect
                id="summary-month"
                name="summary-month"
                options={MONTHS_OPTIONS}
                label="Select Month"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                errorMessage={errors.month}
                values={selectedMonth ? [selectedMonth] : []}
                onChange={(option: Option[]) => {
                  setMonth(option[0]?.value?.toString() || '');
                  setErrors((current) => ({ ...current, month: undefined }));
                  clearDates();
                }}
              />
              <div className="d-flex flex-column">
                <label className="form-label">Date Range</label>
                <div className="d-flex align-items-center">
                  <DatePicker
                    showIcon
                    selected={fromDate}
                    onChange={(date: Date | null) => {
                      setFromDate(date);
                      if (toDate && date && toDate < date) {
                        setToDate(null);
                      }
                      if (date) {
                        clearMonth();
                      }
                    }}
                    placeholderText="From Date"
                    openToDate={fromDate || new Date()}
                    dateFormat="MM/dd/yyyy"
                  />
                  <div className="ms-3">
                    <DatePicker
                      showIcon
                      selected={toDate}
                      onChange={(date: Date | null) => {
                        setToDate(date);
                        if (date) {
                          clearMonth();
                        }
                      }}
                      placeholderText="To Date"
                      minDate={fromDate || undefined}
                      openToDate={toDate || fromDate || new Date()}
                      dateFormat="MM/dd/yyyy"
                    />
                  </div>
                </div>
              </div>
              <CWSelect
                id="summary-location"
                name="summary-location"
                options={locations}
                label="Location"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                errorMessage={errors.location}
                values={selectedLocation ? [selectedLocation] : []}
                onChange={(option: Option[]) => {
                  setLocation(option[0]?.value?.toString() || '');
                  setErrors((current) => ({ ...current, location: undefined }));
                }}
              />
              <div className="button-group d-flex">
                <Button className="primary-btn" variant="primary" title="Generate Report" onClick={generateReport} disabled={loading}>
                  Generate Report
                </Button>
              </div>
            </div>
          </div>
        </div>

        {reportVisible && (
          <>
            <div className="grand-total-card">
              <span className="grand-total-label">
                Grand Total Revenue
                <span className="grand-total-hint">(Sum of PWA revenue and PAX revenue)</span>
              </span>
              <span className="amount">{formatAmount(grandTotal)}</span>
            </div>

            <div className="summary-grid">
              {summary.map((channel) => (
                <section className="channel-card" key={channel.title}>
                  <div className="channel-header">
                    <h5>{channel.title}</h5>
                    <span className="amount">{formatAmount(channel.total)}</span>
                  </div>
                  {channel.sections.length ? channel.sections.map((section) => (
                    <div className="wash-card" key={`${channel.title}-${section.name}`}>
                      <h6>{section.name}</h6>
                      {section.bays.map((bay, index) => (
                        <div className="bay-row" key={`${section.name}-${bay.name}-${index}`}>
                          <span>{bay.name}</span>
                          <span className="amount">{formatAmount(bay.amount)}</span>
                        </div>
                      ))}
                      <div className="section-total amount">{formatAmount(section.subtotal)}</div>
                    </div>
                  )) : (
                    <p className="empty-channel">No revenue found.</p>
                  )}
                </section>
              ))}
            </div>
          </>
        )}
      </Container>
      {loading && <SmallLoader />}
    </div>
  );
};

export default RevenueSummaryReport;
